import { reactionSubmitted } from "@/gtmEvents/reactions";
import useCurrentUser from "@/hooks/useCurrentUser";
import pushToDataLayer from "@/lib/pushToDataLayer";
import useReaction from "@/src/reactions/hooks/useReaction";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { observer } from "mobx-react";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import useGTM from "@/hooks/useGTM";

interface ReactionProps {
  elementId: number;
  isPremiumElement: boolean;
  isNotesEnabled: boolean;
}

interface ReactionButtonProps {
  onClick: () => void;
  disabled: boolean;
  isActive: boolean;
  icon: React.ElementType;
  label: string;
  className?: string;
  activeClassName?: string;
}

const Reaction: React.FC<ReactionProps> = ({
  elementId,
  isPremiumElement = false,
  isNotesEnabled = false,
}) => {
  const { reaction, undoReaction, createReaction, isCreating, isUndoing } =
    useReaction({
      elementId,
    });

  const { currentUser, isFetching, isLoggedIn } = useCurrentUser();
  const [showThanks, setShowThanks] = useState(false);
  const [optimisticReaction, setOptimisticReaction] = useState(reaction); // TODO: useOptimistic when it's available in React

  const gtm = useGTM();

  useEffect(() => {
    setOptimisticReaction(reaction);
  }, [reaction]);

  const handleClick = async (type: "positive" | "negative") => {
    const isUndo = optimisticReaction?.type === type;
    const previousReaction = optimisticReaction;

    setOptimisticReaction(isUndo ? null : { type, uid: "temp-uid" });

    try {
      let response;
      if (isUndo) {
        response = await undoReaction(optimisticReaction.uid);
      } else {
        response = await createReaction({ type });
        setShowThanks(true);
      }

      // Send GTM event
      pushToDataLayer(reactionSubmitted, {
        recipeId: response.reaction?.element?.id,
        recipeSlug: response.reaction?.element?.slug,
        recipeTitle: response.reaction?.element?.title,
        reactionType: type,
      });

      gtm.pushEvent("reaction_submitted", {
        recipeId: response.reaction?.element?.id,
        recipeSlug: response.reaction?.element?.slug,
        recipeTitle: response.reaction?.element?.title,
        reactionType: type,
      });
    } catch (error) {
      console.error(`Failed to ${isUndo ? "undo" : "create"} reaction:`, error);
      setOptimisticReaction(
        isUndo ? { type, uid: optimisticReaction.uid } : previousReaction
      );
      if (!isUndo) setShowThanks(false);
    }
  };

  const userIsPremium = currentUser?.plan === 2;

  const reactionMessageLinkClasses = clsx(
    "underline decoration-zinc-300 underline-offset-2 transition ease-out hover:text-zinc-950 hover:decoration-zinc-400"
  );

  const canReact = (userIsPremium && isPremiumElement) || !isPremiumElement;

  return (
    <div className={clsx("Reaction")}>
      <h3 className={clsx("mb-3 text-center font-spatial text-lg lg:text-2xl")}>
        {showThanks ? "Thanks for your feedback!" : "Did you like this recipe?"}
      </h3>
      <div className={clsx("flex justify-center space-x-4")}>
        <ReactionButton
          onClick={() => handleClick("negative")}
          disabled={!canReact || isCreating || isUndoing}
          isActive={optimisticReaction?.type === "negative"}
          icon={HandThumbDownIcon}
          label="Thumbs Down"
          activeClassName={clsx("bg-red")}
        />
        <ReactionButton
          onClick={() => handleClick("positive")}
          disabled={!canReact || isCreating || isUndoing}
          isActive={optimisticReaction?.type === "positive"}
          icon={HandThumbUpIcon}
          label="Thumbs Up"
          activeClassName={clsx("bg-acid")}
        />
      </div>
      {!isFetching && !isLoggedIn && (
        <div
          className={clsx("mt-4 text-center font-body text-sm text-zinc-500")}
        >
          <Link href="/signin" className={clsx(reactionMessageLinkClasses)}>
            You need to be logged in
          </Link>{" "}
          to share your rating
        </div>
      )}
      {!isFetching && isLoggedIn && !canReact && (
        <div
          className={clsx("mt-4 text-center font-body text-sm text-zinc-500")}
        >
          <Link
            href="/premium#sign-up"
            className={clsx(reactionMessageLinkClasses)}
          >
            Upgrade to a Mob Premium membership
          </Link>{" "}
          to share your rating
        </div>
      )}
      {showThanks && (
        <div
          className={clsx("mt-4 text-center font-body text-sm text-zinc-500")}
        >
          {isNotesEnabled
            ? "Thanks! Share more in the notes below"
            : "Thanks for your feedback!"}
        </div>
      )}
    </div>
  );
};

const ReactionButton: React.FC<ReactionButtonProps> = ({
  onClick,
  disabled,
  isActive,
  icon: Icon,
  activeClassName = "",
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        "reaction-button rounded-full px-5 py-2 transition ease-out lg:px-6 lg:py-3",
        {
          [activeClassName]: isActive,
          "text-zinc-50": isActive,
          "bg-gray-100 ": !isActive,
          "hover:bg-gray-200": !isActive && !disabled,
        }
      )}
    >
      <Icon className={clsx("size-8 lg:size-9")} />
    </button>
  );
};

export default observer(Reaction);
